import type { Language } from '../types';

export type RecipeContentRejectionCode = 'not_a_recipe' | 'inappropriate_content';

export interface LocalRecipeScreenResult {
  allowed: boolean;
  code?: RecipeContentRejectionCode;
}

function normalizeForScreening(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFKC')
    .replace(/[\u0591-\u05C7]/g, '')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ');
}

// Fast local screen for obvious adult / profanity / disgusting words.
// Keep patterns whole-word oriented to avoid false positives.
const INAPPROPRIATE_PATTERNS: readonly RegExp[] = [
  /\b(fuck|fucking|fucker|shit|shitty|bitch|bastard|asshole|dick|pussy|cunt|whore|slut|porn|porno|xxx|nude|nudes|nsfw|blowjob|handjob|cumshot|orgasm|masturbat\w*|hentai|erotic|sex tape)\b/i,
  /\b(זין|זיון|כוסאמק|כוס אמק|מזדיין|מזדיינת|מזיינים|שרמוטה|זונה|זונות|תחת|מחרבן|מניאק|בן זונה|בת זונה|אלף זונות|ציצי|קקי|שתן)\b/u,
  /\b(feces|fecal|poop|poop eating|eat shit|urine|urine drinking)\b/i,
];

const EXTREME_PATTERNS: readonly RegExp[] = [
  /\b(rape|rapist|pedophil\w*|child porn|kill yourself|kys)\b/i,
  /\b(אונס|אנס|ילדים|פדופיל)\b/u,
];

function countPatternMatches(text: string, patterns: readonly RegExp[]): number {
  let count = 0;
  for (const pattern of patterns) {
    if (pattern.test(text)) count += 1;
  }
  return count;
}

export function estimateLocalSeverity(code: RecipeContentRejectionCode, text: string): number {
  if (code === 'not_a_recipe') {
    const len = text.trim().length;
    if (len < 15) return 1;
    if (len < 80) return 2;
    return 3;
  }

  const normalized = normalizeForScreening(text);
  const extremeMatches = countPatternMatches(normalized, EXTREME_PATTERNS);
  const inappropriateMatches = countPatternMatches(normalized, INAPPROPRIATE_PATTERNS);

  let score = 6;
  if (extremeMatches > 0) score = 9;
  else if (inappropriateMatches >= 3) score = 8;
  else if (inappropriateMatches >= 2) score = 7;

  const lenBoost = text.trim().length > 400 ? 1 : 0;
  return Math.min(10, Math.max(6, score + lenBoost));
}

export function screenRecipeTextLocally(text: string): LocalRecipeScreenResult {
  const trimmed = text.trim();
  if (!trimmed) {
    return { allowed: false, code: 'not_a_recipe' };
  }

  const normalized = normalizeForScreening(trimmed);
  for (const pattern of INAPPROPRIATE_PATTERNS) {
    if (pattern.test(normalized)) {
      return { allowed: false, code: 'inappropriate_content' };
    }
  }

  return { allowed: true };
}

export function getLocalRejectionMessage(code: RecipeContentRejectionCode, language: Language): string {
  if (code === 'inappropriate_content') {
    return language === 'he'
      ? 'לא ניתן לנתח תוכן זה. הסירו שפה פוגענית או לא הולמת ונסו שוב.'
      : 'This content cannot be analyzed. Please remove offensive or inappropriate language and try again.';
  }

  return language === 'he'
    ? 'הטקסט לא נראה כמו מתכון. הדביקו מתכון עם מצרכים והוראות הכנה.'
    : 'This text does not look like a recipe. Please paste cooking instructions with ingredients and steps.';
}

